import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@geely/ui";
import { Plus } from 'lucide-react';
import { AdminLayout } from '@/app/components/AdminLayout';
import { PageHeader, PageLoading } from '@/app/components/shared';
import { TablePagination } from '@/app/components/shared/TablePagination';
import { ModelsTable } from './components/ModelsTable';
import { ModelFormDialog } from './components/ModelFormDialog';
import { VersionsTable } from './components/VersionsTable';
import { VersionFormDialog } from './components/VersionFormDialog';
import { useModelsPageData } from './hooks';

export function ModelsPage() {
  const {
    // Models
    models,
    allModels,
    isLoadingModels,
    modelsPaginationMeta,
    setModelsPage,
    isModelDialogOpen,
    editingModel,
    handleOpenModelDialog,
    handleCloseModelDialog,
    handleSubmitModel,
    handleDeleteModel,

    // Versions
    versions,
    isLoadingVersions,
    versionsPaginationMeta,
    setVersionsPage,
    selectedModelFilter,
    setSelectedModelFilter,
    isVersionDialogOpen,
    editingVersion,
    handleOpenVersionDialog,
    handleCloseVersionDialog,
    handleSubmitVersion,
    handleDeleteVersion,
  } = useModelsPageData();

  if (isLoadingModels) {
    return <PageLoading message="Carregando modelos..." />;
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <Tabs defaultValue="models">
          <TabsList>
            <TabsTrigger value="models">Modelos</TabsTrigger>
            <TabsTrigger value="versions">Versões</TabsTrigger>
          </TabsList>

          <TabsContent value="models" className="space-y-6">
            <PageHeader
              title="Gerenciar Modelos"
              subtitle="Cadastre e gerencie os modelos de veículos"
              actions={
                <Button onClick={() => handleOpenModelDialog()}>
                  <Plus className="size-4 mr-2" />
                  Novo Modelo
                </Button>
              }
            />

            <ModelsTable
              models={models}
              onEdit={handleOpenModelDialog}
              onDelete={handleDeleteModel}
            />

            {modelsPaginationMeta && (
              <TablePagination meta={modelsPaginationMeta} onPageChange={setModelsPage} />
            )}

            <ModelFormDialog
              isOpen={isModelDialogOpen}
              editingModel={editingModel}
              onSubmit={handleSubmitModel}
              onClose={handleCloseModelDialog}
            />
          </TabsContent>

          <TabsContent value="versions" className="space-y-6">
            <PageHeader
              title="Gerenciar Versões"
              subtitle="Cadastre e gerencie as versões dos modelos"
              actions={
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <Select
                    value={selectedModelFilter ?? 'all'}
                    onValueChange={(value) =>
                      setSelectedModelFilter(value === 'all' ? undefined : value)
                    }
                  >
                    <SelectTrigger className="w-full sm:w-[220px]">
                      <SelectValue placeholder="Filtrar por modelo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os modelos</SelectItem>
                      {allModels.map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          {model.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => handleOpenVersionDialog()}>
                    <Plus className="size-4 mr-2" />
                    Nova Versão
                  </Button>
                </div>
              }
            />

            {isLoadingVersions ? (
              <PageLoading message="Carregando versões..." />
            ) : (
              <VersionsTable
                versions={versions}
                models={allModels}
                onEdit={handleOpenVersionDialog}
                onDelete={handleDeleteVersion}
              />
            )}

            {versionsPaginationMeta && (
              <TablePagination meta={versionsPaginationMeta} onPageChange={setVersionsPage} />
            )}

            <VersionFormDialog
              isOpen={isVersionDialogOpen}
              editingVersion={editingVersion}
              models={allModels}
              onSubmit={handleSubmitVersion}
              onClose={handleCloseVersionDialog}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
